import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, Map as MapIcon, Columns, PanelTopOpen } from 'lucide-react';
import { Button } from '../components/ui/button';

import Visits from './Visits';
import MapPage from './MapPage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReportsPage — single unified workspace for visit reporting + mapping.
 *
 * Replaces the old Tabs implementation (Table vs Map) and the standalone
 * /map route. Three view modes:
 *   - both:  table on top, map on bottom (side-by-side on >= xl screens)
 *   - table: full-width table, map hidden
 *   - map:   full-width map, table hidden
 *
 * `?view=` keeps the choice persistent across reloads/bookmarks.
 * /map redirects here so old bookmarks survive.
 *
 * Both children (`Visits`, `MapPage`) already manage their own internal state.
 * We pass `hideHeader` so a single page-level header renders at the top.
 */
export default function ReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = useMemo(() => {
    const v = (searchParams.get('view') || '').toLowerCase();
    return v === 'table' || v === 'map' ? v : 'both';
  }, [searchParams]);
  const [view, setView] = useState(initialView);
  const [unitLabel, setUnitLabel] = useState('visit');

  useEffect(() => {
    axios.get(`${API}/tracking-config`).then(res => {
      if (res.data?.configured) {
        setUnitLabel(res.data.unit_label_singular || 'visit');
      }
    }).catch(() => {});
  }, []);

  // Sync the URL when the user changes mode so a refresh keeps the view.
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (view === 'both') {
      next.delete('view');
    } else {
      next.set('view', view);
    }
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Reports</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            View {unitLabel} data alongside the territory map — toggle to focus on either view.
          </p>
        </div>
        <div className="inline-flex bg-muted rounded-lg p-1 gap-1" role="tablist" aria-label="View mode">
          <Button
            variant={view === 'both' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('both')}
            className="gap-1.5 h-8"
            data-testid="reports-view-both"
          >
            <Columns size={14} />
            Both
          </Button>
          <Button
            variant={view === 'table' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('table')}
            className="gap-1.5 h-8"
            data-testid="reports-view-table"
          >
            <FileText size={14} />
            Table
          </Button>
          <Button
            variant={view === 'map' ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setView('map')}
            className="gap-1.5 h-8"
            data-testid="reports-view-map"
          >
            <MapIcon size={14} />
            Map
          </Button>
        </div>
      </div>

      {/* Both view: stacked vertically — the table stays scrollable, the map
          stays sticky in its own frame. xl breakpoint keeps each half on
          ultra-wide monitors. */}
      {view === 'both' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div className="bg-card rounded-lg border overflow-hidden" data-testid="reports-table-pane">
            <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between">
              <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
                <FileText size={14} className="text-primary" /> {unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1)}s
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setView('table')}
                className="h-7 px-2 text-xs gap-1"
                title="Maximize table"
              >
                <PanelTopOpen size={12} />
                Maximize
              </Button>
            </div>
            <div className="p-3 max-h-[78vh] overflow-y-auto">
              <Visits hideHeader={true} />
            </div>
          </div>
          <div className="bg-card rounded-lg border overflow-hidden" data-testid="reports-map-pane">
            <div className="px-4 py-2 border-b bg-muted/30 flex items-center justify-between">
              <h2 className="text-sm font-semibold inline-flex items-center gap-1.5">
                <MapIcon size={14} className="text-primary" /> Map
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setView('map')}
                className="h-7 px-2 text-xs gap-1"
                title="Maximize map"
              >
                <PanelTopOpen size={12} />
                Maximize
              </Button>
            </div>
            <div className="p-3">
              <MapPage hideHeader={true} />
            </div>
          </div>
        </div>
      )}

      {view === 'table' && (
        <div data-testid="reports-table-only">
          <Visits hideHeader={true} />
        </div>
      )}

      {view === 'map' && (
        <div data-testid="reports-map-only">
          <MapPage hideHeader={true} />
        </div>
      )}
    </div>
  );
}
